import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';

function Systems() {
  const { octant, galaxy, quadrant, section } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchSystems();
  }, [offset]);

  const fetchSystems = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/universe/systems?octant=${octant}&galaxy=${galaxy}&quadrant=${quadrant}&section=${section}&offset=${offset}&limit=${limit}`);
      setSystems(response.data.systems);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch systems:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setOffset(prev => prev + limit);
  };

  const getStarColor = (starClass) => {
    const colors = {
      'O': '#7CC6FF',
      'B': '#A0CFFF',
      'A': '#F2F8FF',
      'F': '#FFF4C2',
      'G': '#FFE082',
      'K': '#FFB86A',
      'M': '#FF8A80'
    };
    return colors[starClass] || '#FFF';
  };

  return (
    <div className="min-h-screen pb-20">
      <Header
        breadcrumbs={[
          { label: 'ARC Sectors', link: '/explore/octants' },
          { label: `ARC - ${parseInt(octant) + 1}`, link: `/explore/galaxies/${octant}` },
          { label: `Galaxy-${galaxy}`, link: `/explore/quadrants/${octant}/${galaxy}` },
          { label: `Quadrant-${quadrant}`, link: `/explore/sections/${octant}/${galaxy}/${quadrant}` },
          { label: `Section-${section}` }
        ]}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-2">Systems in Section-{section}</h2>
          <p className="text-muted-foreground">Showing {systems.length} of {total.toLocaleString()} star systems</p>
        </div>

        {loading && offset === 0 ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="systems-grid">
              {systems.map((system) => (
                <button
                  key={system.id}
                  onClick={() => navigate(`/system/${system.coords}`)}
                  className="glass-card rounded-xl p-6 text-left group relative"
                  data-testid={`system-${system.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-primary">{system.name}</h3>
                    <svg className="w-5 h-5 text-cyan-400 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: system.star_color, boxShadow: `0 0 10px ${system.star_color}` }}
                      />
                      <span className="text-sm text-muted-foreground">Star Class: <span className="text-foreground">{system.star_class}</span></span>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      Planets: <span className="text-foreground">{system.planet_count}</span>
                    </p>

                    {system.discovered && (
                      <p className="text-xs text-cyan-400">
                        Discovered by: {system.discoverer}
                      </p>
                    )}

                    {!system.discovered && (
                      <span className="inline-block px-2 py-1 bg-yellow-400/20 text-yellow-400 text-xs rounded mt-2">
                        Undiscovered
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {offset + limit < total && (
              <div className="flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-6 py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg btn-glow disabled:opacity-50 transition-all"
                  data-testid="load-more"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Systems;
